import type { ReactNode } from 'react';
import { Link } from 'react-router';
import {
  TICKET_TRACE_STATUSES,
  type TicketLabel,
  type TicketPriority,
  type TicketStaffListItem,
  type TicketTraceStatus,
} from '@traceio-org/platform-contracts';
import { initialsOf, productShortName } from '../../format';
import { PRIORITY_LABELS, TRACE_STATUS_LABELS, ageOf, dueOf } from './ticketFormat';

export type TicketDisplay = 'list' | 'board';

/** Priority as Linear draws it: three bars (urgent: three red bars), with its name for assistive technology. */
export function PriorityIcon({ priority }: { priority: TicketPriority }) {
  const filled = { urgent: 3, high: 3, medium: 2, low: 1, none: 0 }[priority];
  return (
    <span
      className="admin-priority"
      data-priority={priority}
      title={PRIORITY_LABELS[priority]}
      role="img"
      aria-label={`Priority: ${PRIORITY_LABELS[priority]}`}
    >
      {[0, 1, 2].map((bar) => (
        <i key={bar} data-on={bar < filled || undefined} />
      ))}
    </span>
  );
}

/** A status dot drawn like Linear's (dashed for Triage, filled for Fix in progress, a check-like ring for Done). */
export function StatusDot({ status }: { status: TicketTraceStatus | null }) {
  return <span className="admin-status-dot" data-status={status ?? 'none'} aria-hidden="true" />;
}

export function LabelChip({
  label,
  children,
}: {
  label: Pick<TicketLabel, 'name' | 'color'>;
  children?: ReactNode;
}) {
  return (
    <span className="admin-label-chip">
      <i
        className="admin-label-dot"
        style={{ background: label.color ?? 'currentColor' }}
        aria-hidden="true"
      />
      {label.name}
      {children}
    </span>
  );
}

/**
 * The response target in words (Replied, Reply in 2 h, Overdue 35 min), for a queue row and the ticket page alike: the
 * red tone is only a supplement, the words say Overdue.
 */
export function DueChip({
  item,
  now,
}: {
  item: Pick<TicketStaffListItem, 'firstTraceReplyAt' | 'responseDueAt' | 'overdue'>;
  now: number;
}) {
  const due = dueOf(item, now);
  if (!due) return null;
  return (
    <span className="admin-due" data-tone={due.tone}>
      {due.text}
    </span>
  );
}

function Assignee({ person }: { person: TicketStaffListItem['traceAssignee'] }) {
  const name = person?.name ?? null;
  return (
    <span className="admin-assignee" data-empty={name ? undefined : 'true'} title={name ?? 'Unassigned'}>
      <span aria-hidden="true">{name ? initialsOf(name) : ''}</span>
      <span className="admin-sr-only">{name ? `Assigned to ${name}` : 'Unassigned'}</span>
    </span>
  );
}

/** The queue grouped by Trace status, in the queue's order, showing only the statuses that have tickets. */
export function groupByStatus(items: readonly TicketStaffListItem[]) {
  return TICKET_TRACE_STATUSES.map((status) => ({
    status,
    items: items.filter((item) => item.traceStatus === status),
  })).filter((group) => group.items.length > 0);
}

/**
 * Tickets as Linear lists them: one section per status (h3 with the count), each ticket a link to its page with
 * priority, number, title, labels, customer and app, the response target, assignee and age. The board shows the same
 * groups as columns of cards. Rows are links, so J and K (and Tab) move between them and Enter opens one.
 */
export function TicketList({
  items,
  display,
  now,
  showCustomer = true,
}: {
  items: readonly TicketStaffListItem[];
  display: TicketDisplay;
  now: number;
  /** False on an organization's tab, where every ticket is that customer's. */
  showCustomer?: boolean;
}) {
  const groups = groupByStatus(items);
  if (display === 'board') {
    return (
      <div className="admin-ticket-board">
        {groups.map((group) => (
          <section key={group.status} className="admin-ticket-column" aria-labelledby={`col-${group.status}`}>
            <h3 id={`col-${group.status}`} className="admin-ticket-group__title">
              <StatusDot status={group.status} />
              {TRACE_STATUS_LABELS[group.status]}
              <span className="admin-muted">{group.items.length}</span>
            </h3>
            <ul className="admin-bare-list admin-ticket-cards">
              {group.items.map((item) => (
                <li key={item.id}>
                  <Link className="admin-ticket-card" to={`/tickets/${item.number}`} data-ticket-row>
                    <span className="admin-ticket-card__meta">
                      <span className="admin-ticket-number">{item.number}</span>
                      {showCustomer ? <span>{item.organizationName}</span> : null}
                    </span>
                    <span className="admin-ticket-title">{item.title}</span>
                    <span className="admin-ticket-card__foot">
                      <PriorityIcon priority={item.priority} />
                      <DueChip item={item} now={now} />
                      <Assignee person={item.traceAssignee} />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    );
  }
  return (
    <div className="admin-ticket-list">
      {groups.map((group) => (
        <section key={group.status} aria-labelledby={`group-${group.status}`}>
          <h3
            id={`group-${group.status}`}
            className="admin-ticket-group__title admin-ticket-group__title--band"
          >
            <StatusDot status={group.status} />
            {TRACE_STATUS_LABELS[group.status]}
            <span className="admin-muted">{group.items.length}</span>
          </h3>
          <ul className="admin-bare-list">
            {group.items.map((item) => (
              <li key={item.id}>
                <Link className="admin-ticket-row" to={`/tickets/${item.number}`} data-ticket-row>
                  <PriorityIcon priority={item.priority} />
                  <span className="admin-ticket-number">{item.number}</span>
                  <span className="admin-ticket-title">{item.title}</span>
                  <span className="admin-ticket-labels">
                    {item.labels.map((label) => (
                      <LabelChip key={label.id} label={label} />
                    ))}
                  </span>
                  <span className="admin-ticket-where">
                    {[
                      showCustomer ? item.organizationName : null,
                      item.product ? productShortName(item.product) : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  <DueChip item={item} now={now} />
                  <Assignee person={item.traceAssignee} />
                  <span
                    className="admin-ticket-age"
                    title={`Last activity ${new Date(item.lastActivityAt).toUTCString()}`}
                  >
                    {ageOf(item.lastActivityAt, now)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** J and K: move focus to the next or previous ticket row on the page (wrapping), as Linear does. */
export function moveTicketFocus(direction: 1 | -1): void {
  const rows = Array.from(document.querySelectorAll<HTMLElement>('[data-ticket-row]'));
  if (rows.length === 0) return;
  const current = rows.indexOf(document.activeElement as HTMLElement);
  const next =
    current < 0 ? (direction === 1 ? 0 : rows.length - 1) : (current + direction + rows.length) % rows.length;
  rows[next]?.focus();
}
